import { apiFetch } from "@/client/lib/api";
import type { SessionResponse } from "@/shared/types";

/**
 * Typed client for the auth endpoints. The endpoints themselves are implemented
 * in PE-860; this layer fixes the request/response contract the app shell codes
 * against so wiring the backend is a drop-in.
 */

export interface LoginInput {
  email: string;
  password: string;
}

export interface AcceptInviteInput {
  token: string;
  name: string;
  password: string;
}

export interface ForgotPasswordInput {
  email: string;
}

export interface ResetPasswordInput {
  token: string;
  password: string;
}

export interface VerifyEmailInput {
  token: string;
}

export interface TokenCheckInput {
  token: string;
}

export interface TokenCheckResponse {
  valid: boolean;
}

export interface MfaChallengeResponse {
  mfaRequired: true;
  enrolling: boolean;
  recovery?: true;
}

export type AuthResponse = SessionResponse | MfaChallengeResponse;

export interface MfaEnrollmentResponse {
  secret: string;
  otpauthUri: string;
}

export interface RecoveryCodesResponse {
  recoveryCodes: string[];
}

export const authApi = {
  session: () => apiFetch<SessionResponse>("/auth/session"),
  login: (input: LoginInput) =>
    apiFetch<AuthResponse>("/auth/login", { method: "POST", json: input }),
  logout: () => apiFetch<void>("/auth/logout", { method: "POST" }),
  acceptInvite: (input: AcceptInviteInput) =>
    apiFetch<AuthResponse>("/auth/accept-invite", { method: "POST", json: input }),
  checkInviteToken: (input: TokenCheckInput) =>
    apiFetch<TokenCheckResponse>("/auth/accept-invite/check", { method: "POST", json: input }),
  forgotPassword: (input: ForgotPasswordInput) =>
    apiFetch<void>("/auth/forgot-password", { method: "POST", json: input }),
  resetPassword: (input: ResetPasswordInput) =>
    apiFetch<void>("/auth/reset-password", { method: "POST", json: input }),
  checkResetToken: (input: TokenCheckInput) =>
    apiFetch<TokenCheckResponse>("/auth/reset-password/check", { method: "POST", json: input }),
  verifyEmail: (input: VerifyEmailInput) =>
    apiFetch<void>("/auth/verify-email", { method: "POST", json: input }),
  startMfaEnrollment: () =>
    apiFetch<MfaEnrollmentResponse>("/auth/mfa/enroll/start", { method: "POST" }),
  confirmMfaEnrollment: (code: string) =>
    apiFetch<RecoveryCodesResponse>("/auth/mfa/enroll/confirm", {
      method: "POST",
      json: { code },
    }),
  completeMfaEnrollment: () =>
    apiFetch<SessionResponse>("/auth/mfa/enroll/complete", { method: "POST" }),
  verifyMfa: (code: string) =>
    apiFetch<SessionResponse>("/auth/mfa/verify", { method: "POST", json: { code } }),
  recoverMfa: (code: string) =>
    apiFetch<MfaChallengeResponse>("/auth/mfa/recover", { method: "POST", json: { code } }),
  stepUpMfa: (code: string) =>
    apiFetch<void>("/auth/mfa/step-up", { method: "POST", json: { code } }),
};
